/**
 * Right-column marketing content shown alongside the sign-up form.
 *
 * Stripe's structure: three sections, alternating prose with logo strips.
 * Our copy is platform-agnostic — see root CLAUDE.md §10.1.2 and the
 * brand promise in §1. Logos are text-mark placeholders until we have
 * proper assets.
 */
export function SignUpMarketing() {
  return (
    <div className="space-y-8">
      <Section
        heading="Connect any ecommerce platform"
        body="Pull orders straight from your store. BigCommerce at launch; Shopify, Magento, and custom integrations as we scale."
      >
        <LogoStrip logos={['BigCommerce', 'Shopify', 'Magento', 'Custom']} />
      </Section>

      <Section
        heading="Per-label pricing, no tiers"
        body="Pay only for what you ship. Rule routing, AI assistance, label generation, and end-of-day manifests are all included."
      />

      <Section
        heading="Trusted by growing merchants"
        body="Reliable shipping software designed for the modern ecommerce stack."
      >
        <LogoStrip logos={['Merchant', 'Merchant', 'Merchant']} muted />
      </Section>
    </div>
  );
}

interface SectionProps {
  readonly heading: string;
  readonly body: string;
  readonly children?: React.ReactNode;
}

function Section({ heading, body, children }: SectionProps) {
  return (
    <div className="space-y-3">
      <h3 className="text-foreground text-sm font-semibold">{heading}</h3>
      <p className="text-muted-foreground text-sm">{body}</p>
      {children}
    </div>
  );
}

interface LogoStripProps {
  readonly logos: readonly string[];
  readonly muted?: boolean;
}

/**
 * Placeholder logo strip — renders the names as small pill labels.
 * Swap for real `<img>` / `<svg>` marks when assets land (separate PR).
 */
function LogoStrip({ logos, muted }: LogoStripProps) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      {logos.map((label, i) => (
        <span
          key={`${label}-${String(i)}`}
          className={
            muted
              ? 'border-border text-muted-foreground rounded-md border bg-transparent px-2.5 py-1 text-xs font-medium'
              : 'border-border bg-muted text-foreground rounded-md border px-2.5 py-1 text-xs font-medium'
          }
        >
          {label}
        </span>
      ))}
    </div>
  );
}
